import { useState } from 'react';
import { Bell, CheckCheck, Menu, Moon, Sun, Upload } from 'lucide-react';
import type { AppTheme } from '@/theme/applyTheme';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

export interface HeaderNotification {
  id: string;
  title: string;
  description: string;
  time: string;
  unread?: boolean;
  applicationId?: string | null;
}

interface AppHeaderProps {
  onToggleSidebar: () => void;
  onUploadCV: () => void;
  userName: string;
  userRole: string;
  notifications?: HeaderNotification[];
  onNotificationClick?: (notification: HeaderNotification) => void | Promise<void>;
  onMarkAllAsRead?: () => void | Promise<void>;
  isMarkingAllAsRead?: boolean;
  theme: AppTheme;
  onToggleTheme: () => void;
}

export default function AppHeader({
  onToggleSidebar,
  onUploadCV,
  userName,
  userRole,
  notifications = [],
  onNotificationClick,
  onMarkAllAsRead,
  isMarkingAllAsRead = false,
  theme,
  onToggleTheme,
}: AppHeaderProps) {
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const initials = userName
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const unreadCount = notifications.filter((item) => item.unread).length;
  const hasUnread = unreadCount > 0;

  return (
    <header className="bg-card border-b border-border-subtle px-4 py-3 md:px-6 xl:px-8 shrink-0 z-30 shadow-sm">
      <div className="flex flex-wrap items-center gap-3 md:gap-4">
        <button
          type="button"
          onClick={onToggleSidebar}
          className="inline-flex lg:hidden h-10 w-10 items-center justify-center rounded-lg border border-border-subtle bg-card text-text-main"
        >
          <Menu size={18} />
        </button>

        <div className="order-1 md:order-2 flex items-center gap-2 ml-auto">
          <Button onClick={onUploadCV} variant="outline" size="sm">
            <Upload size={16} />
            <span className="hidden sm:inline">Upload CV</span>
          </Button>
          <Button
            type="button"
            onClick={onToggleTheme}
            variant="outline"
            size="sm"
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </Button>
        </div>

        <div className="order-2 md:order-3 h-8 w-px bg-border-subtle hidden sm:block" />

        <Popover open={isNotificationsOpen} onOpenChange={setIsNotificationsOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="order-2 md:order-3 relative p-2 text-text-subtle hover:text-primary cursor-pointer transition-colors"
              aria-label="Open notifications"
            >
              <Bell size={20} />
              {unreadCount > 0 ? (
                <div className="absolute top-2 right-2 min-w-2 h-2 rounded-full bg-destructive border-2 border-card" />
              ) : null}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" sideOffset={10} className="w-88 p-0 overflow-hidden">
            <div className="border-b border-border-subtle px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-text-main">Notifications</div>
                  <div className="mt-0.5 text-xs text-text-subtle">
                    {unreadCount} unread updates
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => void onMarkAllAsRead?.()}
                  disabled={!hasUnread || isMarkingAllAsRead}
                  className="h-7 px-2 text-xs"
                >
                  <CheckCheck size={14} /> Mark all read
                </Button>
              </div>
            </div>

            <div className="max-h-80 overflow-auto">
              {notifications.length === 0 ? (
                <div className="px-4 py-6 text-sm text-text-subtle text-center">
                  No notifications yet.
                </div>
              ) : (
                notifications.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => {
                      void onNotificationClick?.(item);
                      setIsNotificationsOpen(false);
                    }}
                    className="block w-full cursor-pointer border-b border-border-subtle/60 px-4 py-3 text-left last:border-b-0 hover:bg-bg-main/60"
                  >
                    <div className="flex items-start gap-2">
                      {item.unread ? (
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      ) : (
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-transparent" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-text-main">{item.title}</p>
                        <p className="mt-0.5 text-xs text-text-subtle line-clamp-2">
                          {item.description}
                        </p>
                        <p className="mt-1 text-[11px] text-text-subtle">{item.time}</p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </PopoverContent>
        </Popover>

        <div className="order-2 md:order-4 h-8 w-px bg-border-subtle hidden sm:block" />

        <div className="order-2 md:order-5 flex items-center gap-2 sm:gap-3 sm:pl-2 group ml-auto md:ml-0">
          <div className="text-right hidden sm:block">
            <div className="text-base font-semibold text-text-main">{userName}</div>
            <div className="text-sm text-text-subtle">{userRole}</div>
          </div>
          <div className="w-9 h-9 shrink-0 rounded-full bg-primary-light flex items-center justify-center text-primary font-bold text-base border border-primary/10">
            {initials || 'U'}
          </div>
        </div>
      </div>
    </header>
  );
}
